<template>
  <section>
    <div id="graph-container" class="px-[20px] relative">
      <!-- Chart Explanation Button -->
      <ExplainButton
        v-if="chart && !loading && !hideExplainButton"
        :chart="chart as Chart"
        :context="chartContext"
        @error="handleExplanationError" />
      <div class="w-full h-[250px] relative" :class="{ 'opacity-20': loading }">
        <canvas id="line-chart"></canvas>
      </div>
      <div id="line-legend-container" class="mt-[20px]"></div>
      <RetirementCalculatorChangeAssumptions class="mt-4" v-if="isRetirementCalculator" />
      <button
        v-else-if="!loading"
        class="text-blue-1 px-3 py-2 button-2 inline-flex items-center gap-x-1.5 mt-2"
        @click="handleModalPopUp">
        <ArrowRightIcon class="text-grey-2 h-5 w-5" />
        <span>How we worked this out</span>
      </button>
      <div
        v-show="loading"
        class="rounded flex flex-row justify-center items-center absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <BeatLoader color="#0064FF" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { Chart, ChartConfiguration } from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
import { useDebounceFn } from '@vueuse/core'
import BeatLoader from 'vue-spinner/src/PulseLoader.vue'
import { abbrNum, formatCurrency } from '@/lib/helpers'
import ExplainButton from '@/components/Charts/ExplainButton.vue'
import type { ChartContext } from '@/types/chartExplanation'

import {
  IncomeProjectionChartData,
  IncomeProjectionRawData,
  IncomeProjectionUserMetadata,
  IncomeType,
} from '@/components/ChartJS/IncomeProjection.type.ts'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import HowWeWorkedOutIncomeProjectionModal from '@/components/Modals/HowWeWorkedOutIncomeProjectionModal.vue'
import { useModalStore } from '@/store/pinia/ModalStore.ts'
import RetirementCalculatorChangeAssumptions from '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorChangeAssumptions.vue'

Chart.register(annotationPlugin)

type Props = {
  data: Array<IncomeProjectionChartData>
  raw: Record<IncomeType, Array<IncomeProjectionRawData>>
  userMetadata: IncomeProjectionUserMetadata
  xAxis: Array<number>
  loading: boolean
  hideExplainButton?: boolean
  isRetirementCalculator?: boolean
}
const props = defineProps<Props>()
const chart = ref<unknown | null>(null)
const gridUpperLimit = ref(0)
const TOOLTIP_HEIGHT = 105
const TOOLTIP_WIDTH = 200

const modalStore = useModalStore()

const handleModalPopUp = () => {
  modalStore.openModal(HowWeWorkedOutIncomeProjectionModal, {})
}

/**
 * @TODO: implement collision detection for annotations
 * it'll suck but it's required for mobile otherwise it looks like a dogs breakfast
 */

const userRetirementLine = computed(() => {
  if (!props.userMetadata?.userFirstName) return []

  const userRetAge = props.userMetadata.userRetirementAge
  const user = props.raw.userSuper.find((entity) => entity.age == userRetAge)
  const xValue = user?.year - props.raw.userSuper[0]?.year
  if (!xValue) return []

  // Determine position based on proximity to chart edges
  const chartWidth = props.xAxis.length
  const isNearStart = xValue < chartWidth * 0.3
  const labelPosition = isNearStart ? 'start' : 'end'

  return [
    {
      drawTime: 'afterDatasetsDraw',
      type: 'box',
      xMin: xValue,
      xMax: xValue,
      backgroundColor: 'transparent',
      borderColor: '#70D6FF',
      borderDash: [4, 4],
      z: 90,
    },
    {
      drawTime: 'afterDatasetsDraw',
      type: 'label',
      xValue: xValue,
      yAdjust: -80,
      position: labelPosition,
      backgroundColor: 'rgba(255,255,255,0.60)',
      content: [`${props.userMetadata.userFirstName.toUpperCase()} RETIRES`],
      font: {
        family: 'Raleway',
        size: 12,
        weight: 700,
      },
      z: 99,
    },
  ]
})

const partnerRetirementXLine = computed(() => {
  if (!props.userMetadata?.partnerFirstName) return []

  const firstYearOnChart = props.raw.userSuper[0]?.year
  const partnerRetAge = props.userMetadata.partnerRetirementAge || 60
  const partnerAgeAtFirstYear =
    props.raw.partnerSuper.find((entity) => entity.year == firstYearOnChart)?.age ?? 0
  if (partnerAgeAtFirstYear >= partnerRetAge) return []

  const partner = props.raw.partnerSuper.find((entity) => entity.age == partnerRetAge)
  const xValue = partner?.year - props.raw.partnerSuper[0]?.year
  if (!xValue) return []

  // Determine position based on proximity to chart edges
  const chartWidth = props.xAxis.length
  const isNearStart = xValue < chartWidth * 0.3
  const labelPosition = isNearStart ? 'start' : 'end'

  return [
    {
      type: 'box',
      xMin: xValue,
      xMax: xValue,
      backgroundColor: 'transparent',
      borderColor: '#70D6FF',
      borderDash: [4, 4],
      z: 90,
    },
    {
      type: 'label',
      xValue: xValue,
      yAdjust: -50,
      position: labelPosition,
      backgroundColor: 'rgba(255,255,255,0.60)',
      content: [`${props.userMetadata.partnerFirstName.toUpperCase()} RETIRES`],
      font: {
        family: 'Raleway',
        size: 12,
        weight: 700,
      },
      z: 99,
    },
  ]
})

// Chart context for better explanations
const chartContext = computed<ChartContext>(() => ({
  chartTitle: 'Assets Projection',
  currentAge: props.userMetadata?.userAge,
  retirementAge: props.userMetadata?.userRetirementAge,
  timeframe: `${props.xAxis[0]} - ${props.xAxis[props.xAxis.length - 1]} years`,
  calculationType: 'Asset growth projection',
  chartDescription:
    'Asset projections showing super, property, shares, and other asset growth over time.',
}))

// Handle explanation error
const handleExplanationError = (error: string) => {
  console.error('Chart explanation error:', error)
  // Could show a toast notification here if needed
}

const setupGraph = () => {
  const data = {
    labels: props.xAxis,
    datasets: [...mapData(props.data)],
  }
  setGridUpperLimit()

  return {
    type: 'line',
    data: data,
    options: {
      maintainAspectRatio: false,
      layout: {
        padding: 0,
      },
      interaction: {
        intersect: false,
        mode: 'nearest',
        axis: 'xy',
      },
      scales: {
        x: {
          border: {
            display: false,
          },
          ticks: {
            maxTicksLimit: 10,
          },
          grid: {
            display: false,
          },
          title: {
            display: false,
          },
        },
        y: {
          border: {
            display: false,
          },
          ticks: {
            padding: 5,
            maxTicksLimit: 5,
            callback: function (value: number) {
              return value ? '$' + abbrNum(value) : '$0'
            },
          },
          grid: {
            color: function (context) {
              return context.tick.value < gridUpperLimit.value ? '#70D6FF' : 'transparent'
            },
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: externalTooltipHandler,
        },
        htmlLegend: {
          containerID: 'line-legend-container',
        },
        legend: {
          display: false,
          position: 'bottom',
          align: 'center',
        },
        // ...partnerRetirementXLine.value
        annotation: {
          annotations: [...userRetirementLine.value, ...partnerRetirementXLine.value],
        },
      },
    },
    plugins: [htmlLegendPlugin],
  } as unknown as ChartConfiguration
}

const firstDraw = ref(true)
const drawGraph = useDebounceFn(() => {
  // If chart exists and we have data, update instead of recreating
  if (chart.value && props.data.length > 0) {
    console.log('Assets chart exists, updating data instead of recreating')
    updateData(props.data)
    return
  }

  // Only create new chart if none exists
  console.log('Creating new assets chart instance')
  const config = setupGraph()
  let canvas: HTMLCanvasElement
  nextTick(async () => {
    canvas = document.getElementById('line-chart') as HTMLCanvasElement
  }).then(() => {
    chart.value = new Chart(canvas, config)
    firstDraw.value = false
  })
}, 300)

const setGridUpperLimit = () => {
  props.data.forEach((el) => {
    const tempMaxVal = Math.max(...el.data)
    gridUpperLimit.value = Math.max(tempMaxVal, gridUpperLimit.value)
  })
}

const getOrCreateLegendList = (id: string) => {
  const legendContainer = document.getElementById(id)
  if (!legendContainer) {
    return console.warn('Null HTMLElement found')
  }
  let listContainer = legendContainer.querySelector('div')

  if (!listContainer) {
    listContainer = document.createElement('div')
    listContainer.style.display = 'flex'
    listContainer.style.flexDirection = 'row'
    listContainer.style.flexWrap = 'wrap'
    listContainer.style.justifyContent = 'center'
    listContainer.style.gap = '5px'
    legendContainer.appendChild(listContainer)
  }

  return listContainer
}

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div')

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.style.background = 'rgba(255, 255, 255, 0.9)'
    tooltipEl.style.borderRadius = '12px'
    tooltipEl.style.color = '#000'
    tooltipEl.style.opacity = 1
    tooltipEl.style.padding = '10px'
    tooltipEl.style.pointerEvents = 'none'
    tooltipEl.style.position = 'absolute'
    tooltipEl.style.transform = 'translate(-50%, 0)'
    tooltipEl.style.transition = 'all .1s ease'
    tooltipEl.style.transition = 'all .1s ease'
    tooltipEl.classList.add('bar-tooltip')

    const table = document.createElement('table')
    table.style.margin = '0px'
    table.style.width = TOOLTIP_WIDTH + 'px'
    table.style.height = 'auto'
    table.style.borderCollapse = 'collapse'

    tooltipEl.appendChild(table)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }
  return tooltipEl
}

const externalTooltipHandler = ({ chart, tooltip }) => {
  // Tooltip Element
  // debugger
  const tooltipEl = getOrCreateTooltip(chart)
  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  tooltipEl.classList.remove('top', 'bottom', 'center', 'left', 'right')
  if (tooltip.yAlign || tooltip.xAlign) {
    tooltipEl.classList.add(tooltip.yAlign)
    tooltipEl.classList.add(tooltip.xAlign)
  }

  // Set Text
  if (tooltip.body) {
    const tableBody = document.createElement('tbody')

    // Get x-axis value using the datapoint index
    const dataPointIndex = tooltip.dataPoints[0].dataIndex
    const xAxisValue = props.xAxis[dataPointIndex]

    // Find data by matching either age or year
    const firstKey = Object.keys(props.raw)[0]
    let found = props.raw[firstKey].find((entity) => entity.age == xAxisValue)
    if (!found) {
      // Try matching by year if age match failed
      found = props.raw[firstKey].find((entity) => entity.year == xAxisValue)
    }
    if (!found) {
      return
    }

    const tr = addRow()
    const td = addLabel('Year ', found.year.toString())
    tr.appendChild(td)
    tableBody.appendChild(tr)

    addUserName(tableBody)
    addUserAge(tableBody, found.year, found.age)

    const hasPartner = props.userMetadata.partnerFirstName ?? false
    const processedAssets = new Set()

    Object.keys(props.raw).forEach((assetType) => {
      // Skip already processed assets
      if (processedAssets.has(assetType)) return

      // Skip partner assets - they'll be handled with their corresponding user assets
      if (assetType.includes('partner')) return

      // Debug logging for property assets
      if (assetType === 'property') {
        console.log(`Property in tooltip raw data:`, props.raw[assetType]?.slice(0, 3))
      }

      const assetData = props.raw[assetType]?.find(
        (entity) => entity.age == xAxisValue || entity.year == xAxisValue,
      )

      // Handle totalAssets separately (no partner equivalent)
      if (assetType === 'totalAssets') {
        if (assetData && assetData.projection > 0) {
          addAssetRow(
            tableBody,
            'TOTAL ASSETS',
            formatCurrency(assetData.projection),
            null,
            hasPartner,
          )
          processedAssets.add(assetType)
        }
        return
      }

      // Look for corresponding partner asset (userSuper -> partnerSuper pattern)
      const partnerAssetType = assetType.startsWith('user')
        ? assetType.replace('user', 'partner')
        : 'partner' + assetType.charAt(0).toUpperCase() + assetType.slice(1)

      const partnerAssetData =
        hasPartner && props.raw[partnerAssetType]
          ? props.raw[partnerAssetType].find(
              (entity) => entity.age == xAxisValue || entity.year == xAxisValue,
            )
          : null

      // Show asset if it has data and value > 0
      if (assetData && assetData.projection > 0) {
        let cleanAssetName = assetType
          .replace('user', '')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toUpperCase()

        // Determine if this is a shared/property asset (home or investment property)
        const isSharedAsset = cleanAssetName === 'HOME' || cleanAssetName.includes('PROPERTY')
        const partnerDisplayValue =
          partnerAssetData && partnerAssetData.projection > 0
            ? formatCurrency(partnerAssetData.projection)
            : isSharedAsset
              ? null
              : '$0'

        addAssetRow(
          tableBody,
          cleanAssetName,
          formatCurrency(assetData.projection),
          partnerDisplayValue,
          hasPartner,
        )
        processedAssets.add(assetType)
        processedAssets.add(partnerAssetType)
      }
    })

    const tableRoot = tooltipEl.querySelector('table')

    // Remove old children if tableRoot exists
    if (tableRoot && tableRoot.firstChild) {
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove()
      }
    }

    // Add new children
    if (tableRoot) {
      tableRoot.appendChild(tableBody)
    } else {
      const table = document.createElement('table')
      table.appendChild(tableBody)
      tooltipEl.appendChild(table)
    }
  }

  const chartRighthandBound: number = Math.round(chart.chartArea.right)
  const outsideBounds = chartRighthandBound - (TOOLTIP_WIDTH + 15) <= tooltip.caretX

  const topOffset = TOOLTIP_HEIGHT / 2
  tooltipEl.style.opacity = 1
  tooltipEl.style.position = 'absolute'
  tooltipEl.style.left = outsideBounds
    ? chartRighthandBound - (TOOLTIP_WIDTH / 2 + 7) + 'px'
    : TOOLTIP_WIDTH / 2 + 15 + tooltip.caretX + 'px'
  tooltipEl.style.top = -topOffset + tooltip.caretY + 'px'
  tooltipEl.style.pointerEvents = 'none'
  tooltipEl.style.zIndex = '50'
}

const addColumn = (text: string, textColor: string, width?: string, fontWeight?: string) => {
  const td = document.createElement('td')
  td.style.borderWidth = '0'
  td.style.width = width || '40px'
  td.style.fontSize = '10px'
  const tdSpan = document.createElement('span')
  tdSpan.style.color = textColor
  if (fontWeight) {
    tdSpan.style.fontWeight = fontWeight
  }
  td.appendChild(tdSpan)
  const tdSpanText = document.createTextNode(text)
  tdSpan.appendChild(tdSpanText)
  td.appendChild(tdSpan)
  return td
}

const addLabel = (firstText: string, secondText?: string) => {
  const td = document.createElement('td')
  td.style.borderWidth = '0'
  td.style.fontSize = '10px'
  td.style.color = '#0064FF'

  const span = document.createElement('span')
  span.style.fontWeight = '900'
  const text = document.createTextNode(firstText)
  span.appendChild(text)
  if (secondText) {
    const bold = document.createElement('bold')
    bold.style.fontWeight = '900'
    bold.style.color = '#0037A1'
    const titleText = document.createTextNode(secondText)
    bold.appendChild(titleText)
    span.appendChild(bold)
  }

  td.appendChild(span)
  return td
}

const addRow = () => {
  const tr = document.createElement('tr')
  tr.style.backgroundColor = 'inherit'
  tr.style.borderWidth = '0'
  return tr
}

const addUserName = (tableBody: HTMLTableSectionElement) => {
  const tr = addRow()
  const td1 = addColumn('', '#0037A1', '80px')
  tr.appendChild(td1)

  const td2 = addColumn(props.userMetadata.userFirstName, '#0037A1', '40px', '900')
  tr.appendChild(td2)

  if (props.userMetadata.partnerFirstName) {
    const td = addColumn(props.userMetadata.partnerFirstName, '#0037A1', '40px', '900')
    tr.appendChild(td)
  }

  tableBody.appendChild(tr)
}

const addUserAge = (tableBody: HTMLTableSectionElement, year: number, age: number) => {
  const tr = addRow()
  const td1 = addColumn('AGE', '#0037A1', '80px', '900')
  tr.appendChild(td1)

  const td2 = addColumn(age.toString(), '#0064FF')
  tr.appendChild(td2)

  if (props.raw.partnerSuper) {
    const partner = props.raw.partnerSuper.find((entity) => entity.year == year)
    if (partner) {
      const td = addColumn(partner.age.toString(), '#0064FF')
      tr.appendChild(td)
    }
  }

  tableBody.appendChild(tr)
}

const addAsset = (
  tableBody: HTMLTableSectionElement,
  assetName: string,
  xAxisTitle: string,
  partnerAsset = false,
) => {
  const tr = addRow()
  const td1 = addColumn(assetName, '#0037A1', '80px', '900')
  tr.appendChild(td1)
  if (partnerAsset) {
    const td = addColumn(' ', '#0064FF')
    tr.appendChild(td)
  }
  const td2 = addColumn(xAxisTitle, '#0064FF')
  tr.appendChild(td2)

  tableBody.appendChild(tr)
}

const addAssetRow = (
  tableBody: HTMLTableSectionElement,
  assetName: string,
  userValue: string,
  partnerValue: string | null,
  hasPartner: boolean,
) => {
  const tr = addRow()
  const td1 = addColumn(assetName, '#0037A1', '80px', '900')
  tr.appendChild(td1)

  const td2 = addColumn(userValue, '#0064FF')
  tr.appendChild(td2)

  if (hasPartner) {
    const td3 = addColumn(partnerValue || '', '#0064FF')
    tr.appendChild(td3)
  }

  tableBody.appendChild(tr)
}

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const div = getOrCreateLegendList(options.containerID)
    if (!div) {
      console.warn('LegendList not found')
      return null
    }
    // Remove old legend items
    while (div.firstChild) {
      div.firstChild.remove()
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart)
    items.forEach((item) => {
      const legendItem = document.createElement('div')
      legendItem.style.alignItems = 'center'
      legendItem.style.cursor = 'pointer'
      legendItem.style.display = 'flex'
      legendItem.style.flexDirection = 'row'
      legendItem.style.fontFamily = 'Raleway'
      legendItem.style.flexWrap = 'wrap'
      legendItem.style.padding = '5px'
      legendItem.style.borderRadius = '3px'
      legendItem.style.border = '1px solid #EEEBEB'

      if (item.hidden) {
        // disabled style
        legendItem.style.background = 'white'
        legendItem.style.borderLeft = `3px solid ${item.strokeStyle}`
      } else {
        // enabled style
        legendItem.style.background = item.strokeStyle
        legendItem.style.borderLeft = `3px solid ${item.strokeStyle}`
      }

      legendItem.onclick = () => {
        const { type } = chart.config
        if (type === 'pie' || type === 'doughnut') {
          // Pie and doughnut charts only have a single dataset and visibility is per item
          chart.toggleDataVisibility(item.index)
        } else {
          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
        }
        chart.update()
      }

      // Text
      const textContainer = document.createElement('p')
      textContainer.style.color = item.hidden ? '#888' : '#fff'
      textContainer.style.fontSize = '10px'
      textContainer.style.textTransform = 'uppercase'
      textContainer.style.fontFamily = 'Raleway'
      textContainer.style.fontWeight = 'bold'
      textContainer.style.margin = '0'
      textContainer.style.padding = '0'
      const text = document.createTextNode(item.text)
      textContainer.appendChild(text)
      legendItem.appendChild(textContainer)
      div.appendChild(legendItem)
    })
  },
}

const mapData = (dataset: Array<Record<string, unknown>>) => {
  const colors = ['#0064FF', '#0037A1', '#00885E', '#FD8900', '#F00', '#696969', '#A0A0A0']
  return dataset.map((value, index: number) => {
    value.borderColor = colors[index] || '#A0A0A0'
    value.borderWidth = 3
    value.pointRadius = 0
    value.borderCapStyle = 'round'
    value.order = index
    return value
  })
}

const updateData = (newData: IncomeProjectionChartData[]) => {
  const chart = Object.values(Chart.instances)
    .filter((c) => c.canvas.id == 'line-chart')
    .pop() as Chart

  // Exit early if chart instance doesn't exist yet
  if (!chart || !chart.data || !chart.data.datasets) {
    console.log('Chart instance not ready, skipping update')
    return
  }

  chart.data.labels = props.xAxis

  // Replace all datasets with new ones to handle dynamic asset additions
  const mappedNewData = mapData(newData)
  chart.data.datasets = mappedNewData
  chart.options.plugins.annotation.annotations = [
    ...userRetirementLine.value,
    ...partnerRetirementXLine.value,
  ]

  chart.update()
}

watch(
  () => props.data,
  (data, oldData) => {
    if (JSON.stringify(data) === JSON.stringify(oldData)) return
    if (chart.value) {
      updateData(data)
    } else if (data.length > 0 && !props.loading) {
      // If chart doesn't exist but we have data and not loading, create the chart
      console.log('Creating chart instance as data arrived before chart was initialized')
      drawGraph()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => props.loading,
  (loading, oldValue) => {
    if (!loading && oldValue && firstDraw.value) drawGraph()
  },
)

onBeforeUnmount(() => {
  // cleanup
  const chart = Chart.getChart('line-chart')
  chart?.destroy()
})
</script>

<style>
.bar-tooltip:before {
  content: ' ';
  position: absolute;
  left: -15px;
  top: 40px;
  border: 8px solid transparent;
  border-right-color: rgba(255, 255, 255, 0.9);
}
</style>
